import find from "lodash/find";
import React, {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { State } from "state";
import loadingSvg from "assets/img/loading.svg";
import successSvg from "assets/img/success.svg";
import { Folder } from "lucide-react";
import axios from "axios";
// import {
//   Status,
//   EventTypes,
//   FileType,
//   FileStatus,
//   createAnalyticsEvent,
// } from "state/analytics";
import {
  checkExistingFiles,
  fileCheckFailed,
  getSASURLUpload,
  updateUploadedFiles
} from "state/create_file_upload";
import { fetchProject } from "state/project";

// Import store upload state actions
import {
  setStoreUploadProject,
  setStoreUploadProgress,
  setStoreLoading,
  setStoreError,
  clearStoreUploads,
} from "state/store-upload-state";

import config from "config";
import { Typography } from "@material-ui/core";
import { fetchAllProjectFiles } from "state/files";

interface Step2IndividualUploadProps {
  userId: any;
  projectId: any;
  fileStatus: string;
  projectName?: string;
  projectType?: string;
  onFilesUploaded?: (fileNames: string[]) => void;
  shouldTriggerUpload?: boolean;
  disabled?: boolean;
}

interface AnalyticsData {
  attachmentsCount: number;
  fileExtensionList: string[];
  overwrite: boolean;
  projectName: string;
  projectType: string;
  fileStatus: string;
  fileType: string;
}

const acceptedStoreFileTypes = ".pdf, .psa";

// Helper function to normalize file names by replacing # with __
const normalizeFileName = (name: string): string => {
  return name?.replace(/#/g, '__');
};

const uploadChunk = async (
  chunk: Blob,
  sasUrl: string,
  blockId: string,
  blobAxios: any
) => {
  const arrayBuffer = await chunk.arrayBuffer();
  const uint8Array = new Uint8Array(arrayBuffer);

  const blockUrl = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;

  if (blobAxios.defaults.headers.common["Authorization"]) {
    delete blobAxios.defaults.headers.common["Authorization"];
  }

  await blobAxios.put(blockUrl.toString(), uint8Array);
};

const Step2IndividualUpload = forwardRef<
  { triggerUpload: () => Promise<void> },
  Step2IndividualUploadProps
>(
  (
    {
      userId,
      projectId,
      fileStatus,
      projectName = "",
      projectType = "",
      onFilesUploaded,
      shouldTriggerUpload = false,
      disabled = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const [analytics, setAnalytics] = useState<AnalyticsData | undefined>();
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [finalUploadMessage, setFinalUploadMessage] = useState<string>("");

    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const project = useSelector((state: State) => state.project.content);
    const { hasError, loading, content, errors } = useSelector((state: State) => ({
      hasError: state.ATTACHMENT_file_upload.hasError,
      loading: state.ATTACHMENT_file_upload.loading,
      content: state.ATTACHMENT_file_upload.content,
      errors: state.ATTACHMENT_file_upload.errors,
    }));

    const storeUploadState = useSelector((state: State) => state.storeUpload);
    const { uploadedFiles = [], uploadProgress = {} } = storeUploadState || {};

    useImperativeHandle(ref, () => ({ triggerUpload: handleSubmit }));

    const notifyFilesUploaded = (files: File[]) => {
      if (onFilesUploaded) {
        // Send normalized file names to parent
        onFilesUploaded(files.map((f) => normalizeFileName(f.name)));
      }
    };

    const checkFileList = useCallback(
      async (prefix: string, projectIdParam: number, fileStatusParam: string, uploadedFiles: FileList) => {
        const totalFileSize = Array.from(uploadedFiles).reduce((total, file) => total + file.size, 0);

        let limitBytes = 0;
        const limitStr = config.PROJECT_FILE_UPLOAD_SIZE_LIMIT.toLowerCase();
        if (limitStr.endsWith("mb")) limitBytes = parseInt(limitStr) * 1024 * 1024;
        else if (limitStr.endsWith("gb")) limitBytes = parseInt(limitStr) * 1024 * 1024 * 1024;
        else limitBytes = parseInt(limitStr);

        // if (totalFileSize > limitBytes) {
        //   dispatch(
        //     fileCheckFailed(prefix, `Please select ${(limitBytes / 1024 / 1024 / 1024).toFixed(2)} GB or less of total files.`)
        //   );
        //   return { continue: false };
        // }

        await dispatch(checkExistingFiles(prefix, projectIdParam, fileStatusParam) as any);

        const filesToBeOverwritten: string[] = [];
        if (content && typeof content !== "string") {
          content?.fileList?.forEach((savedFile: any) => {
            const savedFileName = savedFile.split("/").splice(-1, 1)[0];
            const normalizedSavedFileName = normalizeFileName(savedFileName);
            const existingFile = find(uploadedFiles, (file) => normalizeFileName(file?.name) === normalizedSavedFileName);
            if (existingFile) filesToBeOverwritten.push(existingFile.name);
          });
        }

        setAnalytics({
          attachmentsCount: uploadedFiles.length,
          fileExtensionList: Array.from(uploadedFiles).map(({ name }) => name.split(".").pop() || ""),
          overwrite: filesToBeOverwritten.length > 0,
          projectName,
          projectType,
          fileStatus: fileStatusParam,
          fileType: "store",
        });

        if (filesToBeOverwritten.length > 0) {
          const confirmed = window.confirm(`You are about to overwrite ${filesToBeOverwritten.length} file(s).`);
          if (!confirmed) return { continue: false };
        }

        return { continue: true };
      },
      [content, projectName, projectType, dispatch]
    );

    const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
      else setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        const uniqueFiles = Array.from(
          new Map(files.map((f) => [f.name, f])).values()
        );
        setSelectedFiles(uniqueFiles);
        e.dataTransfer.clearData();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const files = Array.from(e.target.files);
        const uniqueFiles = Array.from(
          new Map(files.map((f) => [f.name, f])).values()
        );
        setSelectedFiles(uniqueFiles);
      }
      e.target.value = "";
    };

    // const handleRemoveFile = (index: number) => setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

    const handleClick = () => fileInputRef.current?.click();

    const handleAfterUpload = () => {
      if (analytics) {
        // dispatch(createAnalyticsEvent({
        //   event: EventTypes.UPLOAD_ATTACHMENTS,
        //   eventType: "Action",
        //   projectName: analytics.projectName,
        //   projectType: analytics.projectType,
        //   uploadAttachmentCount: analytics.attachmentsCount,
        //   uploadAttachmentFileExtensionList: analytics.fileExtensionList,
        //   uploadAttachmentStatus: hasError ? Status.FAILURE : Status.SUCCESS,
        //   uploadAttachmentOverwrite: analytics.overwrite,
        //   uploadAttachmentFileType: FileType.STORE,
        //   uploadAttachmentFileStatus: analytics.fileStatus as FileStatus,
        // }));
      }
      setSelectedFiles([]);
      setIsUploading(false);
      setFinalUploadMessage("File(s) have been uploaded successfully.");

      // Clear store uploads after completion
      setTimeout(() => {
        dispatch(clearStoreUploads());
      }, 3000);
    };

    // Upload single file (Helper) 
    const uploadSingleFile = async (
      file: File,
      sasFile: any,
      fileId: string,
      chainId: number
    ) => {
      const normalizedFileName = normalizeFileName(file?.name);
      const blobAxios = axios.create();

      const chunkSize = 8 * 1024 * 1024;
      const blockIds: string[] = [];
      const totalChunks = Math.ceil(file.size / chunkSize);

      for (let start = 0; start < file.size; start += chunkSize) {
        const chunk = file.slice(start, start + chunkSize);
        const blockNumber = blockIds.length + 1;
        const blockId = btoa(
          `block-${String(blockNumber).padStart(6, "0")}`
        );

        blockIds.push(blockId);

        await uploadChunk(chunk, sasFile.url, blockId, blobAxios);

        // Update progress
        const percentComplete = Math.round(
          (blockNumber / totalChunks) * 100
        );

        dispatch(
          setStoreUploadProgress({
            id: fileId,
            name: normalizedFileName,
            percent: Math.min(percentComplete, 95),
          })
        );
      }

      await dispatch(
        updateUploadedFiles(
          projectId,
          fileStatus,
          normalizedFileName,
          chainId
        ) as any
      );

      const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>
        ${blockIds.map((id) => `<Latest>${id}</Latest>`).join("")}
      </BlockList>`;

      const commitUrl = new URL(sasFile.url);
      commitUrl.searchParams.append("comp", "blocklist");

      if (blobAxios.defaults.headers.common["Authorization"]) {
        delete blobAxios.defaults.headers.common["Authorization"];
      }

      await blobAxios.put(commitUrl.toString(), blockListXml, {
        headers: {
          "x-ms-version": "2020-10-02",
          "Content-Type": "application/xml",
          "x-ms-blob-content-type":
            file.type || "application/octet-stream",
        },
      });

      dispatch(
        setStoreUploadProgress({
          id: fileId,
          name: normalizedFileName,
          percent: 100,
        })
      );
    };

    const handleSubmit = async () => {
      if (disabled) return alert("Attachment upload is disabled.");
      if (selectedFiles.length === 0) return alert("Please select at least one file.");
      if (!project?.chain?.id) return alert("Project information not available.");

      setIsUploading(true);

      // Initialize store upload state
      dispatch(setStoreUploadProject(project));
      dispatch(setStoreLoading(true));
      dispatch(setStoreError(false));

      const dataTransfer = new DataTransfer();
      selectedFiles.forEach((file) => dataTransfer.items.add(file));

      try {
        const result = await checkFileList("ATTACHMENT", Number(projectId), fileStatus, dataTransfer.files);
        if (!result?.continue) {
          setIsUploading(false);
          dispatch(setStoreLoading(false));
          return;
        }

        // Normalize file names before sending to API
        const fileNames = selectedFiles.map((f) => normalizeFileName(f.name));
        const files = [...selectedFiles];

        const uploadResult = await dispatch(getSASURLUpload("ATTACHMENT", Number(projectId), project.chain.id, fileStatus, fileNames, userId, fileStatus) as any
        );

        const sasURLs = uploadResult?.sasURLs || uploadResult?.payload?.sasURLs || [];
        const chainId = uploadResult?.chainId || uploadResult?.payload?.chainId || project.chain.id;

        if (sasURLs.length > 0) {
          // Generate unique IDs for all files BEFORE starting upload
          const fileIds = files.map((file, idx) => `${Date.now()}-${idx}-${normalizeFileName(file?.name)}`);

          // Initialize progress for ALL files first
          fileIds.forEach((fileId, idx) => {
            dispatch(setStoreUploadProgress({
              id: fileId,
              name: normalizeFileName(files[idx].name),
              percent: 0
            }));
          });

          // for (let i = 0; i < sasURLs.length; i++) {
          //   const sasFile = sasURLs[i];
          //   const file = files[i];
          //   const fileId = fileIds[i]; // Use pre-generated ID
          //   const normalizedFileName = normalizeFileName(file?.name);

          //   if (!sasFile?.url || !sasFile?.fileName) continue;

          //   if (sasFile.fileName === normalizedFileName) {

          //     const blobAxios = axios.create();
          //     await blobAxios.put(sasFile.url, "", { headers: { "x-ms-blob-type": "BlockBlob" } });

          //     const chunkSize = 8 * 1024 * 1024;
          //     const blockIds: string[] = [];
          //     const totalChunks = Math.ceil(file.size / chunkSize);

          //     for (let start = 0; start < file.size; start += chunkSize) {
          //       const chunk = file.slice(start, start + chunkSize);
          //       const blockNumber = blockIds.length + 1;
          //       const blockId = btoa(`block-${String(blockNumber).padStart(6, "0")}`);
          //       blockIds.push(blockId);

          //       await uploadChunk(chunk, sasFile.url, blockId, blobAxios);

          //       // Update progress after each chunk
          //       const percentComplete = Math.round((blockNumber / totalChunks) * 100);
          //       dispatch(setStoreUploadProgress({
          //         id: fileId,
          //         name: normalizedFileName,
          //         percent: Math.min(percentComplete, 95) // Keep at 95% until commit
          //       }));
          //     }

          //     await dispatch(updateUploadedFiles(projectId, fileStatus, normalizedFileName, chainId) as any);

          //     const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map((id) => `<Latest>${id}</Latest>`).join("")}</BlockList>`;
          //     const commitUrl = new URL(sasFile.url);
          //     commitUrl.searchParams.append("comp", "blocklist");

          //     if (blobAxios.defaults.headers.common["Authorization"]) delete blobAxios.defaults.headers.common["Authorization"];

          //     await blobAxios.put(commitUrl.toString(), blockListXml, {
          //       headers: {
          //         "x-ms-version": "2020-10-02",
          //         "Content-Type": "application/xml",
          //         "x-ms-blob-content-type": file.type || "application/octet-stream",
          //       },
          //     });

          //     dispatch(setStoreUploadProgress({
          //       id: fileId,
          //       name: normalizedFileName,
          //       percent: 100
          //     }));
          //   }
          // }
          await Promise.all(
            files.map((file, index) =>
              uploadSingleFile(
                file,
                sasURLs[index],
                fileIds[index],
                chainId
              )
            )
          );
          await dispatch(fetchProject(projectId) as any);
          await dispatch(fetchAllProjectFiles(Number(projectId)) as any);
        }

        dispatch(setStoreLoading(false));
        handleAfterUpload();
      } catch (err) {
        dispatch(fileCheckFailed("ATTACHMENT", err instanceof Error ? err.message : "Upload failed"));
        dispatch(setStoreError(true));
        dispatch(setStoreLoading(false));
        setIsUploading(false);
      }
    };

    const getTotalFileSize = () => (selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2);

    // Notify parent when files selected
    useEffect(() => {
      if (selectedFiles.length > 0) notifyFilesUploaded(selectedFiles);
    }, [selectedFiles]);

    useEffect(() => {
      if (shouldTriggerUpload && selectedFiles.length > 0 && !disabled) handleSubmit();
    }, [shouldTriggerUpload, selectedFiles, disabled]);

    return (
      <div style={{ padding: 0 }}>
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
          style={{
            border: dragActive ? "3px solid #4CAF50" : "2px dashed #ccc",
            borderRadius: "8px",
            padding: "40px",
            textAlign: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: dragActive ? "#f0f8f0" : "#fafafa",
            transition: "all 0.3s ease",
            margin: "15px 0 0 0",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleChange}
            accept={acceptedStoreFileTypes}
            style={{ display: "none" }}
            disabled={disabled}
          />
          <Folder size={32} style={{ margin: "0 auto 10px", color: "#ccc" }} />
          <p>
            <span style={{ color: "#E5B42F", marginRight: 5 }}>Click here</span>
            to upload or drag and drop Planogram and PDF files here
          </p>
        </div>

        {uploadedFiles.length > 0 && (
          <div
            style={{
              marginTop: 20,
              padding: "15px",
              borderRadius: "12px",
              background: "#ffffff",
              boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Typography style={{ fontSize: 12, marginBottom: 8, fontWeight: 600, color: "#444" }}>
              Uploading Store Files
            </Typography>
            <ul
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                listStyle: "none",
                padding: 0,
                margin: 0,
              }}
            >
              {uploadedFiles.map((file: any, index: any) => {
                const percent = uploadProgress[file.id] || 0;
                const isComplete = percent === 100;
                return (
                  <li
                    key={index}
                    style={{
                      position: "relative",
                      flex: "0 0 calc(33.33% - 8px)",
                      padding: "8px 12px",
                      background: "#f9f9f9",
                      borderRadius: 8,
                      border: "1px solid #eee",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {isComplete && (
                      <span
                        onClick={() => dispatch(clearStoreUploads())}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          cursor: "pointer",
                          fontWeight: "bold",
                          color: "#ff4d4f",
                          fontSize: 14,
                        }}
                      >
                        ✕
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: 12,
                        marginBottom: 4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      📎 {file?.name}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {loading && <div style={{ textAlign: "center", padding: "20px" }}><img className="spin-right" src={loadingSvg} alt="Loading" /></div>}
        {hasError && <div style={{ padding: "12px", backgroundColor: "#ffebee", border: "1px solid #ef5350", borderRadius: "4px", marginTop: "16px" }}><span style={{ color: "#c62828", fontWeight: 500 }}>Upload Error{errors ? `: ${errors[0]}` : ""}</span></div>}
        {content === "OK" && <div style={{ padding: "16px", backgroundColor: "#e8f5e9", border: "1px solid #4CAF50", borderRadius: "4px", marginTop: "16px" }}><div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}><img src={successSvg} alt="Success" style={{ marginRight: "8px" }} /><div style={{ color: "#2e7d32", fontWeight: 600 }}>Successfully uploaded files.</div></div></div>}
        {finalUploadMessage && <div style={{ marginTop: "20px", padding: "12px 16px", backgroundColor: "#e8f5e9", border: "1px solid #4CAF50", borderRadius: "6px", color: "#2e7d32", fontWeight: 600, textAlign: "center" }}>{finalUploadMessage}</div>}
      </div>
    );
  }
);

Step2IndividualUpload.displayName = "Step2IndividualUpload";
export default Step2IndividualUpload;
